import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Clock, Calendar } from 'lucide-react';
import { seasonalAdventures, testimonials } from '../mock';
import ExperienceCard from '../components/ExperienceCard';
import NewsletterSignup from '../components/NewsletterSignup';
import { Button } from '../components/ui/button';

const Home = () => {
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    "https://images.unsplash.com/photo-1531366936337-7c912a4589a7",
    "https://images.unsplash.com/photo-1483347756197-71ef80e95f73",
    "https://images.unsplash.com/photo-1475518845976-0fd87b7e4e5d",
    "https://images.pexels.com/photos/23897319/pexels-photo-23897319.jpeg"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Images with Crossfade */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-2000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt="Northern Lights"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight">
            We follow the light
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-light mb-8 leading-relaxed">
            When it dances in the sky, and when it shapes the land.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/northern-lights">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-base">
                Explore Northern Lights
              </Button>
            </Link>
            <Button
              onClick={() => setNewsletterOpen(true)}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-base bg-transparent"
            >
              Get Free Aurora Guide
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white" data-testid="who-we-are-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm uppercase tracking-[0.2em] text-brand-teal font-medium">
            Who we are
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-brand-navy mt-4 mb-6 leading-tight">
            Small groups. Extraordinary places. Memories that last.
          </h2>
          <p className="text-lg text-brand-muted max-w-3xl mx-auto leading-relaxed">
            Light Chasing Adventures is built around one belief: that the best experiences happen when everything is personal. Small groups, carefully chosen locations, and a guide who knows your name and your goals before you even arrive. Whether you're a seasoned photographer or you've never shot anything beyond your phone camera — you belong here.
          </p>
        </div>
      </section>

      {/* Featured Tours */}
      <section className="py-20 bg-brand-light" data-testid="featured-tours-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Featured tour card */}
            <div
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-brand-teal/20 flex flex-col"
              data-testid="featured-tour-card"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_e7d1381f-c6bc-43b9-a64c-5f521d4d18e9/artifacts/b66psys3_DSC00535%20%282%29%20-%20Edited.jpg"
                  alt="Arctic Aurora over Tromsø"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-brand-mint text-brand-navy text-xs font-medium px-3 py-1 rounded-full">
                  Featured trip
                </div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center text-sm text-brand-muted mb-2">
                  <MapPin size={16} className="mr-1.5 text-brand-teal" />
                  <span>Tromsø, Norway</span>
                </div>
                <h3 className="text-2xl font-normal text-brand-navy mb-3">
                  Arctic Aurora Adventure
                </h3>
                <p className="text-brand-muted leading-relaxed mb-6">
                  Five nights in Arctic Norway during the September equinox — one of the most reliable times of year for aurora activity. Small group, relaxed pace, and completely personalised around your goals. Your smartphone is all you need.
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-brand-text mb-6">
                  <span className="flex items-center">
                    <Clock size={16} className="mr-1.5 text-brand-teal" />5 nights
                  </span>
                  <span className="flex items-center">
                    <Users size={16} className="mr-1.5 text-brand-teal" />Max 4 guests
                  </span>
                  <span className="flex items-center">
                    <Calendar size={16} className="mr-1.5 text-brand-teal" />September 20–25
                  </span>
                </div>
                <div className="flex items-center justify-between pt-6 border-t border-gray-100 mt-auto">
                  <span className="text-3xl font-light text-brand-navy">£2,000</span>
                  <Link to="/northern-lights-tromso">
                    <Button
                      className="bg-brand-teal hover:bg-brand-teal-dark text-white px-6"
                      data-testid="featured-find-out-more-btn"
                    >
                      Find out more
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Teaser card */}
            <div
              className="group relative bg-brand-navy rounded-2xl overflow-hidden shadow-sm flex flex-col"
              data-testid="teaser-tour-card"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_e7d1381f-c6bc-43b9-a64c-5f521d4d18e9/artifacts/tvbmesdl_I%20love%20this.jpg"
                  alt="Sutherland, Scottish Highlands"
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
                <div className="absolute top-4 left-4 bg-brand-purple text-white text-xs font-medium px-3 py-1 rounded-full">
                  Coming soon
                </div>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center text-sm text-brand-mint mb-2">
                  <MapPin size={16} className="mr-1.5" />
                  <span>Sutherland, Scottish Highlands</span>
                </div>
                <h3 className="text-2xl font-normal text-white mb-3">
                  Highland Photographers Weekend
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Coming soon — an intimate photographers weekend in one of Scotland's most spectacular and unspoilt landscapes. A private cottage, extraordinary scenery, and expert guidance on your doorstep.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Adventures */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-brand-navy mb-6">
              When the nights grow lighter
            </h2>
            <p className="text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed">
              We shift with the seasons — into landscapes shaped by weather, atmosphere, and changing light. Photography adventures in beautiful places, without the intimidation or price tag of traditional tours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {seasonalAdventures.slice(0, 4).map((adventure) => (
              <ExperienceCard key={adventure.id} experience={adventure} type="seasonal" />
            ))}
          </div>

          <div className="text-center">
            <Link to="/seasonal-adventures">
              <Button className="bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-6 text-base">
                Explore Seasonal Adventures
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Teaser */}
      <section className="py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1667504485890-ec335b29b5a6"
                alt="Atmospheric landscape"
                className="rounded-lg shadow-lg w-full h-96 object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-light text-brand-navy mb-6">
                Learning by doing, together
              </h2>
              <p className="text-lg text-brand-muted leading-relaxed mb-6">
                Photography is about noticing light and place. Technical settings are secondary to experience. We create safe, inclusive environments where confidence grows through participation, not perfection.
              </p>
              <p className="text-lg text-brand-muted leading-relaxed mb-8">
                This isn't about luxury travel or gear worship. It's about curiosity, creativity, and making extraordinary places accessible to everyone.
              </p>
              <Link to="/philosophy">
                <Button variant="outline" className="border-2 border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white px-8 py-6 text-base bg-transparent">
                  Read Our Philosophy
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-brand-navy mb-4">
              Voices from the Journey
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-brand-light p-8 rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300">
                <p className="text-brand-text leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-brand-mint/40 pt-4">
                  <p className="font-medium text-brand-navy">{testimonial.name}</p>
                  <p className="text-sm text-brand-muted">{testimonial.location}</p>
                  <p className="text-sm text-brand-teal mt-1">{testimonial.experience}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-6">
            Start Your Aurora Journey
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Get the free <strong>Aurora Readiness Guide</strong> — practical tips for planning your Northern Lights adventure, including best times to visit, what to pack, and photography basics.
          </p>
          <Button
            onClick={() => setNewsletterOpen(true)}
            className="bg-brand-mint text-brand-navy hover:bg-white px-8 py-6 text-base"
          >
            Download Free Guide
          </Button>
        </div>
      </section>

      <NewsletterSignup isOpen={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
    </div>
  );
};

export default Home;